import React, { useState } from 'react';
import { BadgeTypeModel, ApiKeyModel, BadgeTypeCounterItem } from '../types';
import { api } from '../services/api';
import { KeyRound, Copy, Check, Trash2, Terminal, Play, X, Plus } from 'lucide-react';

interface ApiManagementViewProps {
  badgeTypes: BadgeTypeModel[];
  counters: BadgeTypeCounterItem[];
  apiKeys: ApiKeyModel[];
  totalEntered: number;
  onRefresh: () => void;
  preselectedBadgeTypeId?: string;
}

export const ApiManagementView: React.FC<ApiManagementViewProps> = ({
  badgeTypes,
  counters,
  apiKeys,
  totalEntered,
  onRefresh,
  preselectedBadgeTypeId,
}) => {
  const [createdSecret, setCreatedSecret] = useState<{ rawKey: string; name: string } | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  // Live Tester
  const [testEndpoint, setTestEndpoint] = useState('/api/v1/counters/total');
  const [testToken, setTestToken] = useState('');
  const [testResult, setTestResult] = useState<any>(null);
  const [testLoading, setTestLoading] = useState(false);

  const copyText = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(id);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const handleGenerate = async (name: string, type: 'badge_type' | 'total', badgeTypeId?: string) => {
    try {
      const res = await api.createApiKey(name, type, badgeTypeId);
      setCreatedSecret({
        rawKey: res.rawKey,
        name: res.apiKey.name,
      });
      setTestToken(res.rawKey);
      if (type === 'badge_type' && badgeTypeId) {
        setTestEndpoint(`/api/v1/badgetypes/${badgeTypeId}/counter`);
      } else {
        setTestEndpoint('/api/v1/counters/total');
      }
      onRefresh();
    } catch (e: any) {
      alert(`Error generating key: ${e.message}`);
    }
  };

  const handleDeleteKey = async (id: string, name: string) => {
    if (!confirm(`Revoke API key "${name}"? External requests using this key will immediately fail.`)) return;
    try {
      await api.deleteApiKey(id);
      onRefresh();
    } catch (e: any) {
      alert(`Error deleting key: ${e.message}`);
    }
  };

  const runTest = async () => {
    if (!testToken.trim()) {
      alert('Please enter or generate a Bearer API Key first.');
      return;
    }
    setTestLoading(true);
    setTestResult(null);
    try {
      const res = await fetch(testEndpoint, {
        headers: {
          Authorization: `Bearer ${testToken.trim()}`,
        },
      });
      const data = await res.json();
      setTestResult({
        status: res.status,
        data,
      });
    } catch (e: any) {
      setTestResult({
        status: 500,
        error: e.message,
      });
    } finally {
      setTestLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Top Header */}
      <div className="pb-2 border-b border-slate-800">
        <h1 className="text-lg font-bold text-white">API Counters</h1>
        <p className="text-xs text-slate-400">
          Independent REST API endpoints for external platforms. Each counter shares the same source of truth as the live dashboard.
        </p>
      </div>

      {/* One-time Secret Alert */}
      {createdSecret && (
        <div className="p-4 rounded-xl bg-emerald-950/60 border border-emerald-500 text-emerald-200 text-xs space-y-2">
          <div className="flex items-center justify-between font-bold">
            <span className="flex items-center gap-1.5">
              <KeyRound className="w-4 h-4 text-emerald-400" />
              <span>New API Key Generated — Copy Now</span>
            </span>
            <button onClick={() => setCreatedSecret(null)} className="text-emerald-400 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-[11px] text-emerald-300">
            For security, raw keys are never stored in plaintext. Copy this token now for <strong>{createdSecret.name}</strong>:
          </p>
          <div className="flex items-center gap-2 p-2 bg-slate-950 rounded-lg border border-emerald-500/30">
            <code className="flex-1 font-mono text-emerald-400 truncate">{createdSecret.rawKey}</code>
            <button
              onClick={() => copyText(createdSecret.rawKey, 'banner-key')}
              className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-500 text-white rounded text-[11px] font-medium"
            >
              {copiedKey === 'banner-key' ? 'Copied' : 'Copy Key'}
            </button>
          </div>
        </div>
      )}

      {/* 1. Global Total Counter API Card */}
      <div className="p-5 rounded-xl bg-slate-900 border border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="text-[11px] font-mono text-slate-400 uppercase tracking-wider">
            GLOBAL TOTAL COUNTER
          </div>
          <div className="text-xl font-bold text-white mt-0.5">Total Attendance API</div>
          <div className="text-xs text-slate-400 mt-1">
            Current Counter: <strong className="font-mono text-white">{totalEntered}</strong>
          </div>
          <div className="text-[11px] font-mono text-cyan-400 mt-1">
            GET /api/v1/counters/total
          </div>
        </div>

        <button
          onClick={() => handleGenerate('Global Total Video Wall API', 'total')}
          className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold rounded-lg transition-colors shadow-sm self-start sm:self-auto"
        >
          Generate Total API
        </button>
      </div>

      {/* 2. Badge Type Counters List */}
      <div className="space-y-3">
        <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-1">
          Independent Badge Type Counters
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {badgeTypes.map((bt) => {
            const counter = counters.find((c) => c.id === bt.id);
            const currentCount = counter ? counter.scannedCount : 0;
            const existingKey = apiKeys.find((k) => k.type === 'badge_type' && k.badgeTypeId === bt.id);

            return (
              <div
                key={bt.id}
                className="p-4 rounded-xl bg-slate-900 border border-slate-800 space-y-3 hover:border-slate-700 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: bt.color }} />
                    <span className="font-bold text-sm text-white">{bt.name}</span>
                  </div>
                  <span className="text-xs font-mono font-bold text-slate-200">
                    Counter: <span className="text-emerald-400">{currentCount}</span>
                  </span>
                </div>

                <div className="text-[11px] font-mono text-slate-400 bg-slate-950 p-2 rounded truncate">
                  GET /api/v1/badgetypes/{bt.id}/counter
                </div>

                <div className="flex items-center justify-between pt-1">
                  {existingKey ? (
                    <span className="text-[11px] text-emerald-400 font-mono">
                      Active Key: {existingKey.maskedKey}
                    </span>
                  ) : (
                    <span className="text-[11px] text-slate-500 font-mono">No API Key yet</span>
                  )}

                  <button
                    onClick={() => handleGenerate(`${bt.name} Counter API`, 'badge_type', bt.id)}
                    className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-cyan-300 text-xs font-medium rounded-lg border border-slate-700 transition-colors"
                  >
                    Generate API
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 3. Provisioned API Keys List */}
      {apiKeys.length > 0 && (
        <div className="p-5 rounded-xl bg-slate-900 border border-slate-800 space-y-3">
          <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
            Provisioned Keys ({apiKeys.length})
          </div>

          <div className="divide-y divide-slate-800 text-xs">
            {apiKeys.map((k) => (
              <div key={k.id} className="py-2.5 flex items-center justify-between gap-2">
                <div>
                  <span className="font-semibold text-white">{k.name}</span>
                  <span className="text-slate-400 ml-2 font-mono text-[11px]">[{k.maskedKey}]</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleDeleteKey(k.id, k.name)}
                    className="p-1 text-slate-400 hover:text-rose-400 rounded transition-colors"
                    title="Revoke Key"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 4. Live In-Browser API Tester */}
      <div className="p-5 rounded-xl bg-slate-900 border border-slate-800 space-y-3 text-xs">
        <div className="flex items-center gap-2">
          <Terminal className="w-4 h-4 text-emerald-400" />
          <h3 className="font-bold text-white">Live API Endpoint Tester</h3>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-slate-400 block mb-1">Target Endpoint:</label>
            <select
              value={testEndpoint}
              onChange={(e) => setTestEndpoint(e.target.value)}
              className="w-full bg-slate-950 border border-slate-800 rounded-lg px-2.5 py-1.5 text-white font-mono focus:outline-none"
            >
              <option value="/api/v1/counters/total">GET /api/v1/counters/total</option>
              {badgeTypes.map((bt) => (
                <option key={bt.id} value={`/api/v1/badgetypes/${bt.id}/counter`}>
                  GET /api/v1/badgetypes/{bt.name.toLowerCase()}/counter
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-slate-400 block mb-1">Bearer API Key Token:</label>
            <input
              type="text"
              value={testToken}
              onChange={(e) => setTestToken(e.target.value)}
              placeholder="evt_live_..."
              className="w-full bg-slate-950 border border-slate-800 rounded-lg px-2.5 py-1.5 text-white font-mono placeholder-slate-600 focus:outline-none"
            />
          </div>
        </div>

        <button
          onClick={runTest}
          disabled={testLoading}
          className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50"
        >
          <Play className="w-3.5 h-3.5 fill-current" />
          <span>{testLoading ? 'Querying...' : 'Test Request'}</span>
        </button>

        {testResult && (
          <div className="mt-3 p-3 rounded-lg bg-slate-950 border border-slate-800 font-mono text-[11px] space-y-1">
            <div className="text-slate-400">
              HTTP Status: <strong className="text-white">{testResult.status}</strong>
            </div>
            <pre className="text-emerald-400">{JSON.stringify(testResult.data || testResult.error, null, 2)}</pre>
          </div>
        )}
      </div>
    </div>
  );
};
